/**
 * Terminais de Caixa Module Routes — granular permissions (2026-04-21)
 */

import { lazy } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ModuleRoutes } from "../types";

const TerminaisLista = lazy(() => import("@/pages/terminais/Lista"));
const TerminaisCadastro = lazy(() => import("@/pages/terminais/Cadastro"));
const TerminaisDetalhes = lazy(() => import("@/pages/terminais/Detalhes"));

/**
 * Redirect curto: `/terminais/:id` → `/terminais/detalhes/:id`.
 * Lida com URLs antigas/bookmarks/links externos que usam o padrão REST
 * `/terminais/{id}` em vez do explícito `/terminais/detalhes/{id}`.
 * Sem isso, o usuário cai no NotFound.
 */
const TerminaisIdRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/terminais/detalhes/${id}`} replace />;
};
const TerminaisVinculacao = lazy(() => import("@/pages/terminais/Vinculacao"));
// Produção mensal do REL103 e comissão (E19, 2026-09-01).
const TerminaisProducao = lazy(() => import("@/pages/terminais/Producao"));
const TerminaisComissao = lazy(() => import("@/pages/terminais/Comissao"));
const TerminaisSinistros = lazy(() => import("@/pages/terminais/Sinistros"));
const TerminaisAuditoria = lazy(() => import("@/pages/terminais/Auditoria"));
const PeripheralDashboardPage = lazy(() => import("@/pages/terminais/PeripheralDashboardPage"));
const TerminaisLogsOcorrencias = lazy(() => import("@/pages/terminais/LogsOcorrencias"));

export const terminaisRoutes: ModuleRoutes = {
  moduleId: "terminais",
  basePath: "/terminais",
  routes: [
    {
      path: "/terminais/lista",
      element: <TerminaisLista />,
      permission: { resource: "terminais_lista", action: "view" },
    },
    {
      path: "/terminais/cadastro",
      element: <TerminaisCadastro />,
      permission: { resource: "terminais_cadastro", action: "manage" },
    },
    {
      // 2026-04-26: Cadastro reusado em modo edit. Mesma página detecta :id.
      path: "/terminais/editar/:id",
      element: <TerminaisCadastro />,
      permission: { resource: "terminais_cadastro", action: "manage" },
    },
    {
      // ⚠️ Antes de `/terminais/:id`: aquele redirect engoliria `producao`.
      path: "/terminais/producao",
      element: <TerminaisProducao />,
      permission: { resource: "terminais_lista", action: "view" },
    },
    {
      path: "/terminais/comissao/:ano/:mes",
      element: <TerminaisComissao />,
      permission: { resource: "terminais_lista", action: "view" },
    },
    {
      path: "/terminais/detalhes/:id",
      element: <TerminaisDetalhes />,
      permission: { resource: "terminais_lista", action: "view" },
    },
    {
      // Alias REST-style — bookmarks/links externos para `/terminais/{id}`
      // redirecionam pra rota oficial de detalhes (evita NotFound).
      // Precisa vir DEPOIS das rotas estáticas (lista/cadastro/...) pra
      // não engolir caminhos que começam com `/terminais/<palavra>`.
      path: "/terminais/:id",
      element: <TerminaisIdRedirect />,
      permission: { resource: "terminais_lista", action: "view" },
    },
    {
      path: "/terminais/vinculacao/:terminalId",
      element: <TerminaisVinculacao />,
      permission: { resource: "terminais_cadastro", action: "manage" },
    },
    {
      path: "/terminais/sinistros",
      element: <TerminaisSinistros />,
      permission: { resource: "terminais_sinistros", action: "view" },
    },
    {
      path: "/terminais/sinistros/registro",
      element: <TerminaisSinistros />,
      permission: { resource: "terminais_sinistros", action: "manage" },
    },
    {
      path: "/terminais/auditoria",
      element: <TerminaisAuditoria />,
      permission: { resource: "terminais_auditoria", action: "view" },
    },
    {
      path: "/terminais/dashboard-perifericos",
      element: <PeripheralDashboardPage />,
      permission: { resource: "terminais_dashboard", action: "view" },
    },
    {
      path: "/terminais/logs-ocorrencias",
      element: <TerminaisLogsOcorrencias />,
      permission: { resource: "terminais_lista", action: "view" },
    },
  ],
};
